import { useMemo } from 'react';
import { useHabitStore } from '@/stores/habitStore';
import type { Habit } from '@/types';
import { toDateKey } from '@/lib/dateKey';
import { DAY_LETTER, DAY_LONG, MONTH_SHORT } from '@/lib/dateNames';
import { isHabitDueOnDay } from '@/lib/week';
import { useUIStore } from '@/stores/uiStore';

export const useHabitsForToday = (now: Date = new Date()): Habit[] => {
  const habits = useHabitStore((s) => s.habits);
  const weekStart = useUIStore((s) => s.weekStart);
  const dow = now.getDay();
  return useMemo(
    () => habits.filter((h) => isHabitDueOnDay(h, dow, weekStart)),
    [habits, dow, weekStart],
  );
};

export const useScheduledHabitsForDate = (date: Date): Habit[] => {
  const habits = useHabitStore((s) => s.habits);
  const weekStart = useUIStore((s) => s.weekStart);
  const dow = date.getDay();
  return useMemo(
    () => habits.filter((h) => h.scheduledTime && isHabitDueOnDay(h, dow, weekStart)),
    [habits, dow, weekStart],
  );
};

export interface DayCell {
  iso: string;
  date: Date;
  dow: string; // 'M' | 'T' | 'W' | 'T' | 'F' | 'S' | 'S'
  label: string; // 'Monday, May 13'
  done: boolean;
  isToday: boolean;
}

export const useLast7Days = (habitId: string, now: Date = new Date()): DayCell[] => {
  const completions = useHabitStore((s) => s.completions);
  const todayKey = toDateKey(now);
  return useMemo(() => {
    const done = new Set(completions.filter((c) => c.habitId === habitId).map((c) => c.date));
    const cells: DayCell[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const iso = toDateKey(d);
      cells.push({
        iso,
        date: d,
        dow: DAY_LETTER[d.getDay()],
        label: `${DAY_LONG[d.getDay()]}, ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`,
        done: done.has(iso),
        isToday: iso === todayKey,
      });
    }
    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completions, habitId, todayKey]); // now is derived from todayKey; including it would cause needless re-renders
};
